import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign } from "lucide-react";

interface BasePricingFormProps {
  namePrefix?: string;
  format?: string;
  currency?: string;
}

export const BasePricingForm = ({
  namePrefix = "pricing_details",
  format = "Standard",
  currency = "USD",
}: BasePricingFormProps) => {
  const { control, watch } = useFormContext();
  
  // Watch pricing model field
  const pricingModel = watch(`${namePrefix}.pricing_model`);

  return (
    <div className="space-y-4 border p-4 rounded-md bg-background/80">
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="h-5 w-5 text-primary" />
        <h2 className="text-base font-semibold">
          Base Pricing ({format} Format)
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name={`${namePrefix}.pricing_model`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pricing Model</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select pricing model" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="per_volunteer">
                    Cost per Volunteer
                  </SelectItem>
                  <SelectItem value="per_event">Cost per Event</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${namePrefix}.cost_per_unit`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {pricingModel === "per_volunteer"
                  ? "Cost per Volunteer"
                  : "Cost per Event"}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  {currency === "USD" && <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                  {currency === "EUR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">€</span>}
                  {currency === "GBP" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">£</span>}
                  {currency === "JPY" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">¥</span>}
                  {currency === "CAD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">C$</span>}
                  {currency === "AUD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">A$</span>}
                  {currency === "SGD" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">S$</span>}
                  {currency === "INR" && <span className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground">₹</span>}
                  {!["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "SGD", "INR"].includes(currency) && 
                    <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />}
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-8"
                    value={field.value?.toString() || ""}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="catalog_data.min_volunteers"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Minimum Volunteers</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="number" 
                  min="1" 
                  value={field.value?.toString() || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="catalog_data.max_volunteers"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Maximum Volunteers</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="number" 
                  min="1" 
                  value={field.value?.toString() || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
